/**
 * Minimum fixed-dollar coupon discount, in cents, that qualifies a $0 invoice
 * to be activated without a payment method. Small fixed-dollar coupons like
 * the $20 referral coupon don't qualify; percent-off and time-based coupons
 * always do.
 */
export const NO_CARD_MIN_AMOUNT_OFF_CENTS = 4000;

/**
 * Loose invoice shape used by the eligibility check. Stripe's full
 * `Stripe.Invoice` type has so much recursion that it doesn't unify across
 * module boundaries (see the TS comment in CheckoutView.vue); we only need a
 * handful of fields, so we declare the minimum here.
 */
export interface EligibilityInvoice {
  amount_due: number;
  // `customer` may be a string id, a Customer, or a DeletedCustomer. We only
  // read `balance`, so we treat it as opaque and probe at runtime.
  customer?: unknown;
  discounts?: ReadonlyArray<unknown> | null;
}

interface CouponLike {
  amount_off?: number | null;
  percent_off?: number | null;
}

function resolveCoupon(discount: unknown): CouponLike | null {
  if (!discount || typeof discount === 'string') return null;
  const d = discount as Record<string, unknown>;
  if (d.coupon && typeof d.coupon === 'object') return d.coupon as CouponLike;
  const src = d.source as { coupon?: unknown } | null | undefined;
  if (src?.coupon && typeof src.coupon === 'object') return src.coupon as CouponLike;
  return null;
}

/**
 * Context the invoice alone can't tell us.
 */
export interface EligibilityOptions {
  /**
   * The checkout changes the plan of a live, active subscription (not a new
   * subscription, a trial conversion, or a currency switch).
   */
  isPlanChange?: boolean;
}

/**
 * True when the given upcoming invoice can be activated without a payment
 * method. Rules:
 *
 *   - `amount_due` must be exactly 0.
 *   - A plan change on a live subscription needs nothing more: Stripe still
 *     raises the $0 invoice to record the change, but there is nothing to
 *     collect, so we don't ask for a payment method (DAB-957). A downgrade
 *     zeroed by proration credit is the common case.
 *   - Otherwise a coupon-backed discount must be applied (raw credit balances don't
 *     qualify — credits always require a card on file).
 *   - The customer must not be applying stored credit (`balance < 0`).
 *   - The coupon must not be a small fixed-dollar coupon
 *     (`amount_off < NO_CARD_MIN_AMOUNT_OFF_CENTS`). Percent-off and
 *     time-based coupons always pass.
 */
export function canSkipPaymentForInvoice(
  invoice: EligibilityInvoice | null | undefined,
  { isPlanChange = false }: EligibilityOptions = {},
): boolean {
  if (!invoice || invoice.amount_due !== 0) return false;
  if (isPlanChange) return true;

  const cust = invoice.customer;
  if (cust && typeof cust === 'object') {
    const balance = (cust as { balance?: number | null }).balance ?? 0;
    if (balance < 0) return false;
  }

  const coupon = resolveCoupon(invoice.discounts?.[0]);
  if (!coupon) return false;

  if (coupon.amount_off != null && coupon.amount_off < NO_CARD_MIN_AMOUNT_OFF_CENTS) return false;
  return true;
}
